import React, { useEffect, useState } from "react";

import { useAppAuth } from "../hooks";

import NoPermission from "./NoPermission";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string; // Primary permission check - if provided, user must have this permission
  requirePerfectServeUser?: boolean; // If true, PerfectServe users get automatic access
  alternativePermission?: string; // DEPRECATED: Use requiredPermission instead. For OR conditions: requirePerfectServeUser OR alternativePermission
  customPermissionCheck?: (isPerfectServeUser: boolean, hasPermission: (permission: string) => boolean) => boolean;
  feature?: string;
}

const ProtectedRoute = ({
  children,
  requiredPermission,
  requirePerfectServeUser = false,
  alternativePermission,
  customPermissionCheck,
  feature = "this feature",
}: ProtectedRouteProps) => {
  const {
    isPerfectServeUser,
    isPermissionsLoading,
    permissions,
    canAccessApp,
    needsRoleSelection,
    loading,
    isAuthenticated,
    user,
    isUserConfigurationReady,
    refreshPermissions,
    permissionsError,
  } = useAppAuth();
  const [showRetry, setShowRetry] = useState(false);

  // Show retry option after 10 seconds of loading or if there's a permissions error
  useEffect(() => {
    if (isPermissionsLoading || (!canAccessApp && !needsRoleSelection)) {
      const timer = setTimeout(() => {
        setShowRetry(true);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    } else {
      setShowRetry(false);
    }
  }, [isPermissionsLoading, canAccessApp, needsRoleSelection]);

  const handleRetry = async () => {
    setShowRetry(false);
    try {
      await refreshPermissions();
    } catch (error) {
      console.error("Failed to refresh permissions:", error);
    }
  };

  // Show loading state while permissions are being fetched OR app is not ready
  // But don't show loading if the user just needs to select a role
  if (isPermissionsLoading || (!canAccessApp && !needsRoleSelection)) {
    // More specific loading messages based on what's actually loading
    let loadingMessage = "Loading permissions...";

    if (!isAuthenticated) {
      loadingMessage = "Authenticating...";
    } else if (loading) {
      loadingMessage = "Loading user data...";
    } else if (!user) {
      loadingMessage = "Loading user profile...";
    } else if (!isUserConfigurationReady) {
      loadingMessage = "Setting up user configuration...";
    } else if (isPermissionsLoading) {
      loadingMessage = "Loading permissions...";
    }

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          fontSize: "1rem",
          color: "#6c757d",
          gap: "1rem",
        }}
      >
        <div>{loadingMessage}</div>
        {permissionsError && (
          <div style={{ color: "#dc3545", fontSize: "0.9rem", textAlign: "center", maxWidth: "400px" }}>
            Error: {permissionsError}
          </div>
        )}
        {(showRetry || permissionsError) && (
          <button
            onClick={handleRetry}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // If user needs to select a role, let the App component handle showing the role modal
  // by returning null (this allows the role modal to show through)
  if (needsRoleSelection) {
    return null;
  }

  // Check custom permission logic first (most flexible)
  if (customPermissionCheck) {
    // Use direct array check to avoid race conditions with hasPermission function
    const workingHasPermission = (perm: string) => permissions.includes(perm);
    const hasAccess = customPermissionCheck(isPerfectServeUser, workingHasPermission);
    if (!hasAccess) {
      return <NoPermission feature={feature} />;
    }
    // If custom check passes, render the protected content
    return <>{children}</>;
  }

  // Main permission logic: PerfectServe users OR required permission
  if (requirePerfectServeUser || requiredPermission) {
    let hasAccess = false;

    // PerfectServe users get automatic access (if requirePerfectServeUser is true)
    if (requirePerfectServeUser && isPerfectServeUser) {
      hasAccess = true;
    }

    // Check required permission (works for both standalone and OR with PerfectServe)
    // Use direct array check to avoid race conditions with hasPermission function
    if (requiredPermission && permissions.includes(requiredPermission)) {
      hasAccess = true;
    }

    // DEPRECATED: Support legacy alternativePermission (remove after migration)
    // Use direct array check to avoid race conditions with hasPermission function
    if (alternativePermission && permissions.includes(alternativePermission)) {
      hasAccess = true;
    }

    if (!hasAccess) {
      return <NoPermission feature={feature} />;
    }

    // If access is granted, render the protected content
    return <>{children}</>;
  }

  // User has required permissions, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;
